import { useState, useEffect } from "react";
import { Car } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useLanguage, translations } from "@/contexts/LanguageContext";
import { supabase } from "@/integrations/supabase/client";

interface FormData {
  name: string;
  phone: string;
  lineId: string;
  email: string;
  brand: string;
  model: string;
  year: string;
  mileageKm: string;
  askingPriceThb: string;
  transmission: string;
  fuelType: string;
  color: string;
  location: string;
  notes: string;
  photoLinks: string;
}

const initialFormData: FormData = {
  name: "",
  phone: "",
  lineId: "",
  email: "",
  brand: "",
  model: "",
  year: "",
  mileageKm: "",
  askingPriceThb: "",
  transmission: "",
  fuelType: "",
  color: "",
  location: "",
  notes: "",
  photoLinks: "",
};

export function SellCarDialog() {
  const [open, setOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const { language } = useLanguage();
  const t = translations[language];

  // Load saved contact info from localStorage
  useEffect(() => {
    const savedName = localStorage.getItem('ts_contact_name') || '';
    const savedPhone = localStorage.getItem('ts_contact_phone') || '';
    setFormData(prev => ({
      ...prev,
      name: savedName,
      phone: savedPhone,
    }));
  }, []);

  const handleInputChange = (field: keyof FormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const validateForm = (): boolean => {
    if (!formData.name.trim()) {
      toast.error(t.sellCarNameRequired);
      return false;
    }
    if (!formData.phone.trim()) {
      toast.error(t.sellCarPhoneRequired);
      return false;
    }
    const phoneRegex = /^[\d\-+\s]{8,20}$/;
    if (!phoneRegex.test(formData.phone.trim())) {
      toast.error(t.sellCarPhoneInvalid);
      return false;
    }
    if (!formData.brand.trim()) {
      toast.error(t.sellCarBrandRequired);
      return false;
    }
    if (!formData.model.trim()) {
      toast.error(t.sellCarModelRequired);
      return false;
    }
    if (!formData.year.trim()) {
      toast.error(t.sellCarYearRequired);
      return false;
    }
    const year = parseInt(formData.year, 10);
    if (isNaN(year) || year < 1900 || year > new Date().getFullYear() + 1) {
      toast.error(t.sellCarYearInvalid);
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      // Save contact info for future use
      if (formData.name.trim()) {
        localStorage.setItem('ts_contact_name', formData.name.trim());
      }
      if (formData.phone.trim()) {
        localStorage.setItem('ts_contact_phone', formData.phone.trim());
      }

      const { data, error } = await supabase.functions.invoke('submit-car-for-sale', {
        body: {
          name: formData.name.trim(),
          phone: formData.phone.trim(),
          line_id: formData.lineId.trim() || undefined,
          email: formData.email.trim() || undefined,
          brand: formData.brand.trim(),
          model: formData.model.trim(),
          year: parseInt(formData.year, 10),
          mileage_km: formData.mileageKm ? parseInt(formData.mileageKm, 10) : undefined,
          asking_price_thb: formData.askingPriceThb ? parseInt(formData.askingPriceThb.replace(/,/g, ''), 10) : undefined,
          transmission: formData.transmission || undefined,
          fuel_type: formData.fuelType || undefined,
          color: formData.color.trim() || undefined,
          location: formData.location.trim() || undefined,
          notes: formData.notes.trim() || undefined,
          photo_links: formData.photoLinks.trim() || undefined,
          device: navigator.userAgent,
        },
      });

      if (error) {
        console.error('Submission error:', error);
        toast.error(t.sellCarSubmitError);
        return;
      }

      toast.success(t.sellCarSubmitSuccess);
      setFormData({
        ...initialFormData,
        name: formData.name,
        phone: formData.phone,
      });
      setOpen(false);
    } catch (err) {
      console.error('Submission error:', err);
      toast.error(t.sellCarSubmitError);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="default" size="sm" className="gap-2">
          <Car className="h-4 w-4" />
          {t.sellCar}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t.sellCarTitle}</DialogTitle>
          <p className="text-sm text-muted-foreground">{t.sellCarSubtitle}</p>
        </DialogHeader>
        
        <div className="space-y-6 pt-4">
          {/* Contact Information Section */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-foreground border-b pb-2">{t.sellCarContactSection}</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="sell-name">{t.sellCarName} *</Label>
                <Input
                  id="sell-name"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  placeholder={t.namePlaceholder}
                  maxLength={100}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="sell-phone">{t.sellCarPhone} *</Label>
                <Input
                  id="sell-phone"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value.replace(/[^\d\-+\s]/g, ''))}
                  placeholder={t.phonePlaceholder}
                  maxLength={20}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="sell-line">{t.sellCarLineId}</Label>
                <Input
                  id="sell-line"
                  value={formData.lineId}
                  onChange={(e) => handleInputChange('lineId', e.target.value)}
                  placeholder={t.sellCarLineIdPlaceholder}
                  maxLength={50}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="sell-email">{t.sellCarEmail}</Label>
                <Input
                  id="sell-email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  placeholder={t.sellCarEmailPlaceholder}
                  maxLength={255}
                />
              </div>
            </div>
          </div>

          {/* Car Details Section */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-foreground border-b pb-2">{t.sellCarDetailsSection}</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="sell-brand">{t.sellCarBrand} *</Label>
                <Input
                  id="sell-brand"
                  value={formData.brand}
                  onChange={(e) => handleInputChange('brand', e.target.value)}
                  placeholder={t.sellCarBrandPlaceholder}
                  maxLength={50}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="sell-model">{t.sellCarModel} *</Label>
                <Input
                  id="sell-model"
                  value={formData.model}
                  onChange={(e) => handleInputChange('model', e.target.value)}
                  placeholder={t.sellCarModelPlaceholder}
                  maxLength={100}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="sell-year">{t.sellCarYear} *</Label>
                <Input
                  id="sell-year"
                  type="number"
                  value={formData.year}
                  onChange={(e) => handleInputChange('year', e.target.value)}
                  placeholder={t.sellCarYearPlaceholder}
                  min={1900}
                  max={new Date().getFullYear() + 1}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="sell-mileage">{t.sellCarMileage}</Label>
                <Input
                  id="sell-mileage"
                  type="number"
                  value={formData.mileageKm}
                  onChange={(e) => handleInputChange('mileageKm', e.target.value)}
                  placeholder={t.sellCarMileagePlaceholder}
                  min={0}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="sell-price">{t.sellCarPrice}</Label>
                <Input
                  id="sell-price"
                  value={formData.askingPriceThb}
                  onChange={(e) => handleInputChange('askingPriceThb', e.target.value.replace(/[^\d,]/g, ''))}
                  placeholder={t.sellCarPricePlaceholder}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="sell-transmission">{t.sellCarTransmission}</Label>
                <Select value={formData.transmission} onValueChange={(v) => handleInputChange('transmission', v)}>
                  <SelectTrigger id="sell-transmission">
                    <SelectValue placeholder={t.sellCarSelectPlaceholder} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="automatic">{t.automatic}</SelectItem>
                    <SelectItem value="manual">{t.manual}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="sell-fuel">{t.sellCarFuelType}</Label>
                <Select value={formData.fuelType} onValueChange={(v) => handleInputChange('fuelType', v)}>
                  <SelectTrigger id="sell-fuel">
                    <SelectValue placeholder={t.sellCarSelectPlaceholder} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="petrol">{t.gasoline}</SelectItem>
                    <SelectItem value="diesel">{t.diesel}</SelectItem>
                    <SelectItem value="hybrid">{t.hybrid}</SelectItem>
                    <SelectItem value="electric">{t.electric}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="sell-color">{t.sellCarColor}</Label>
                <Input
                  id="sell-color"
                  value={formData.color}
                  onChange={(e) => handleInputChange('color', e.target.value)}
                  placeholder={t.sellCarColorPlaceholder}
                  maxLength={50}
                />
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label htmlFor="sell-location">{t.sellCarLocation}</Label>
                <Input
                  id="sell-location"
                  value={formData.location}
                  onChange={(e) => handleInputChange('location', e.target.value)}
                  placeholder={t.sellCarLocationPlaceholder}
                  maxLength={100}
                />
              </div>
            </div>
          </div>

          {/* Additional Info Section */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-foreground border-b pb-2">{t.sellCarAdditionalSection}</h3>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="sell-notes">{t.sellCarNotes}</Label>
                <Textarea
                  id="sell-notes"
                  value={formData.notes}
                  onChange={(e) => handleInputChange('notes', e.target.value)}
                  placeholder={t.sellCarNotesPlaceholder}
                  rows={3}
                  maxLength={1000}
                  className="resize-none"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="sell-photos">{t.sellCarPhotoLinks}</Label>
                <Textarea
                  id="sell-photos"
                  value={formData.photoLinks}
                  onChange={(e) => handleInputChange('photoLinks', e.target.value)}
                  placeholder={t.sellCarPhotoLinksPlaceholder}
                  rows={2}
                  maxLength={2000}
                  className="resize-none"
                />
              </div>
            </div>
          </div>

          {/* Submit Buttons */}
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setOpen(false)} disabled={isSubmitting}>
              {t.cancel}
            </Button>
            <Button onClick={handleSubmit} disabled={isSubmitting}>
              {isSubmitting ? t.sellCarSubmitting : t.sellCarSubmit}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
